import type { Any } from '@/shared/constant/any';
import type { ICodeSample } from '@tarslib/types/lib/httpApi';
import cloneDeep from 'lodash-es/cloneDeep';
import { v4 as uuid } from 'uuid';
import type { HttpApiRemoteType, HttpApiServerType } from './httpApi';

export enum CurlVariant {
  Window = 'cURL-Windows',
  Linux = 'cURL',
}

const languageMap: Record<
  string,
  {
    key: string;
    label: string;
    syntaxMode: string;
    variants: {
      id?: string;
      key: string;
      name?: string;
      prompt?: string;
      code?: string;
    }[];
    sort?: number;
  }
> = {
  shell: {
    key: 'shell',
    label: 'Shell',
    syntaxMode: 'powershell',
    variants: [
      {
        key: CurlVariant.Linux,
      },
      {
        key: CurlVariant.Window,
      },
      {
        key: 'Httpie',
      },
      {
        key: 'wget',
      },
      {
        key: 'PowerShell',
      },
    ],
  },
  javascript: {
    key: 'javascript',
    label: 'JavaScript',
    syntaxMode: 'javascript',
    variants: [
      {
        key: 'Fetch',
      },
      {
        key: 'Axios',
      },
      {
        key: 'jQuery',
      },
      {
        key: 'XHR',
      },
      {
        key: 'Native',
      },
      {
        key: 'Request',
      },
      {
        key: 'Unirest',
      },
    ],
  },
  java: {
    key: 'java',
    label: 'Java',
    syntaxMode: 'java',
    variants: [{ key: 'Unirest' }, { key: 'OkHttp' }],
  },
  swift: {
    key: 'swift',
    label: 'Swift',
    syntaxMode: 'swift',
    variants: [
      {
        key: 'URLSession',
      },
    ],
  },
  go: {
    key: 'go',
    label: 'Go',
    syntaxMode: 'go',
    variants: [
      {
        key: 'Native',
      },
    ],
  },
  php: {
    key: 'php',
    label: 'PHP',
    syntaxMode: 'php',
    variants: [
      {
        key: 'cURL',
      },
      {
        key: 'HTTP_Request2',
      },
      {
        key: 'pecl_http',
      },
      {
        key: 'Guzzle',
      },
    ],
  },
  python: {
    key: 'python',
    label: 'Python',
    syntaxMode: 'python',
    variants: [
      {
        key: 'http.client',
      },
      {
        key: 'Requests',
      },
    ],
  },
  http: {
    key: 'http',
    label: 'HTTP',
    syntaxMode: 'text',
    variants: [
      {
        key: 'HTTP',
      },
    ],
  },
  c: {
    key: 'c',
    label: 'C',
    syntaxMode: 'cpp',
    variants: [
      {
        key: 'libcurl',
      },
    ],
  },
  csharp: {
    key: 'csharp',
    label: 'C#',
    syntaxMode: 'csharp',
    variants: [
      {
        key: 'RestSharp',
      },
    ],
  },
  'objective-c': {
    key: 'objective-c',
    label: 'Objective-C',
    syntaxMode: 'objective-c',
    variants: [
      {
        key: 'NSURLSession',
      },
    ],
  },
  ruby: {
    key: 'ruby',
    label: 'Ruby',
    syntaxMode: 'ruby',
    variants: [
      {
        key: 'Net::HTTP',
      },
    ],
  },
  ocaml: {
    key: 'ocaml',
    label: 'OCaml',
    syntaxMode: 'ocaml',
    variants: [
      {
        key: 'Cohttp',
      },
    ],
  },
  dart: {
    key: 'dart',
    label: 'Dart',
    syntaxMode: 'dart',
    variants: [
      {
        key: 'http',
      },
    ],
  },
  r: {
    key: 'r',
    label: 'R',
    syntaxMode: 'r',
    variants: [
      {
        key: 'httr',
      },
      {
        key: 'RCurl',
      },
    ],
  },
};

function getLanguages(codeSamples: ICodeSample[]) {
  const newLanguageMap = cloneDeep(languageMap);
  const newCodeSamples = cloneDeep(codeSamples);
  let i = 1;
  newCodeSamples.forEach(({ language }) => {
    if (newLanguageMap[language] && !newLanguageMap[language]?.sort) {
      newLanguageMap[language].sort = i++;
    }
  });
  const currentLanguageMap = newCodeSamples
    .reverse()
    .reduce((pre, { language, name, prompt, code, id }) => {
      if (pre[language]) {
        pre[language].variants.unshift({
          id: String(id),
          key: String(id),
          name,
          prompt,
          code,
        });
      }
      return pre;
    }, newLanguageMap);
  const languages = Object.values(currentLanguageMap);
  return [
    ...languages
      .filter((language) => language?.sort)
      .sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0)),
    ...languages.filter((language) => !language?.sort),
  ];
}

export function transformHttpApi(data: HttpApiRemoteType): HttpApiServerType {
  const {
    parameters,
    commonParameters,
    requestBody,
    codeSamples,
    responses,
    responseChildren = [],
    ...rest
  } = data;

  const query =
    parameters?.query?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];
  const path =
    parameters?.path?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];
  const cookie =
    parameters?.cookie?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];
  const header =
    parameters?.header?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];

  const newCodeSamples = codeSamples.filter((x) => x.id && languageMap[x.language]) ?? [];
  const languages = getLanguages(newCodeSamples);
  const newResponses = responses
    .map((x) => {
      return {
        ...x,
        headers:
          x.headers?.map((y) => ({
            ...y,
            isCommonParameter: false,
            value: (y as typeof Any).sampleValue || y.example,
          })) ?? [],
        key: uuid(),
        id: (() => {
          if (typeof x.id === 'number') {
            return x.id;
          }
          const idString = String(x.id).replace('tempId-', '');
          const idNumber = Number(idString);
          if (Number.isNaN(idNumber)) {
            return undefined;
          } else {
            return idNumber;
          }
        })(),
      };
    })
    .filter((x) => typeof x.id === 'number');
  const newResponseChildren = responseChildren.map((x) => String(x).replace('tempId-', ''));

  const requestMediaType = requestBody.mediaType || requestBody.type;
  const examples = (requestBody.examples ?? [])
    .filter((x) => x.mediaType === requestMediaType)
    .map((x, i) => {
      return {
        ...x,
        key: `${i}`,
      };
    });
  if (!examples.length) {
    const example = requestBody.data || requestBody.example || requestBody.sampleValue;
    if (example) {
      examples.push({
        value: example,
        description: '',
        mediaType: requestMediaType,
        key: uuid(),
      });
    }
  }

  return {
    ...rest,
    parameters: {
      query,
      path,
      cookie,
      header,
    },
    commonParameters: {
      query: commonParameters?.query ?? [],
      body: commonParameters?.body ?? [],
      header: commonParameters?.header ?? [],
      cookie: commonParameters?.cookie ?? [],
    },
    requestBody: {
      ...requestBody,
      examples,
      data:
        requestBody?.data ||
        requestBody?.sampleValue ||
        requestBody?.example ||
        examples[0]?.value ||
        '',
      parameters:
        requestBody?.parameters?.map((x) => ({
          ...x,
          isCommonParameter: false,
          value: (x as typeof Any).sampleValue || x.example,
        })) ?? [],
    },
    codeSamples: newCodeSamples,
    codeSampleLanguages: languages,
    responses: newResponses,
    oasExtensions: data.oasExtensions || '',
    responseChildren: newResponseChildren,
  };
}
